import type {
	Settings,
	EmailGroupingMode,
} from "@/shared/cache/slices/settings.types";
import {
	SettingGroup,
	SettingRow,
	StepInput,
	type NumericInput,
} from "../SettingsShared";
import { Switch } from "@/shared/components/ui/switch";

function InternalEmailToggles({
	trackInternal,
	includeInNarratives,
	update,
}: {
	trackInternal: boolean;
	includeInNarratives: boolean;
	update: (patch: Partial<Settings>) => void;
}) {
	return (
		<>
			<SettingRow
				label="Track internal emails"
				description="Track emails where all participants are within your organization"
			>
				<Switch
					checked={trackInternal}
					onCheckedChange={(v) =>
						// Force include=false when track goes off — the names switch is hidden in that state,
						// so we don't want to leave a stale `true` in the cache that disagrees with the UI.
						update(
							v
								? { p_track_internal_emails: true }
								: { p_track_internal_emails: false, p_include_internal_in_narratives: false },
						)
					}
				/>
			</SettingRow>
			{trackInternal && (
				<SettingRow
					label="Include internal names in narratives"
					description="When tracking internal emails, allow names of internal participants to appear in the generated narrative. When off, internal names are scrubbed."
				>
					<Switch
						checked={includeInNarratives}
						onCheckedChange={(v) =>
							update({ p_include_internal_in_narratives: v })
						}
					/>
				</SettingRow>
			)}
		</>
	);
}

function EmailGroupingToggles({
	mode,
	update,
}: {
	mode: EmailGroupingMode;
	update: (patch: Partial<Settings>) => void;
}) {
	const groupDrafts =
		mode === "combined" || mode === "grouped" || mode === "individual_review";
	const groupReviews =
		mode === "combined" || mode === "grouped" || mode === "individual_draft";
	const combineAll = mode === "combined";

	const resolveMode = (
		drafts: boolean,
		reviews: boolean,
		combine: boolean,
	): EmailGroupingMode => {
		if (combine) return "combined";
		if (drafts && reviews) return "grouped";
		if (!drafts && reviews) return "individual_draft";
		if (drafts && !reviews) return "individual_review";
		return "individual";
	};

	return (
		<>
			<SettingRow
				label="One entry per thread"
				description="All email activity in a thread — drafting and reviewing — is merged into a single time entry."
			>
				<Switch
					checked={combineAll}
					onCheckedChange={(v) =>
						update({
							email_grouping_mode: v
								? "combined"
								: resolveMode(groupDrafts, groupReviews, false),
						})
					}
				/>
			</SettingRow>
			{!combineAll && (
				<>
					<SettingRow
						label="One entry per thread for drafts"
						description="Composing multiple emails in the same thread produces one time entry instead of one per email."
					>
						<Switch
							checked={groupDrafts}
							onCheckedChange={(v) =>
								update({
									email_grouping_mode: resolveMode(v, groupReviews, false),
								})
							}
						/>
					</SettingRow>
					<SettingRow
						label="One entry per thread for reviews"
						description="Reading multiple emails in the same thread produces one time entry instead of one per email."
					>
						<Switch
							checked={groupReviews}
							onCheckedChange={(v) =>
								update({
									email_grouping_mode: resolveMode(groupDrafts, v, false),
								})
							}
						/>
					</SettingRow>
				</>
			)}
		</>
	);
}

export interface TrackingSettingsProps {
	settings: Settings;
	update: (patch: Partial<Settings>) => void;
	emailTime: NumericInput;
	docTime: NumericInput;
	websiteTime: NumericInput;
	inactivityThreshold: NumericInput;
	meetingTime: NumericInput;
	wordCount: NumericInput;
}

export function TrackingSettings({
	settings,
	update,
	emailTime,
	docTime,
	websiteTime,
	inactivityThreshold,
	meetingTime,
	wordCount,
}: TrackingSettingsProps) {
	return (
		<>
			<SettingGroup label="Narratives">
				<SettingRow
					label="Draft detailed narratives"
					description="Generate longer, more detailed narrative descriptions for each entry"
				>
					<Switch
						checked={settings.insurance_defense_mode}
						onCheckedChange={(v) => update({ insurance_defense_mode: v })}
					/>
				</SettingRow>
				{!settings.insurance_defense_mode && (
					<>
						<SettingRow
							label="Check for restricted words"
							description="Flag narratives that contain words from your restricted words list"
						>
							<Switch
								checked={settings.check_for_red_words}
								onCheckedChange={(v) => update({ check_for_red_words: v })}
							/>
						</SettingRow>
						<SettingRow
							label="Show task breakdown in combined entries"
							description={
								settings.block_bill
									? "Each task's time and description shown separately in the narrative."
									: "All tasks combined into a single cohesive narrative paragraph."
							}
						>
							<Switch
								checked={settings.block_bill}
								onCheckedChange={(v) => update({ block_bill: v })}
							/>
						</SettingRow>
						<SettingRow
							label="Max word count"
							description="Maximum number of words allowed in a generated narrative"
						>
							<StepInput {...wordCount} />
						</SettingRow>
					</>
				)}
				<SettingRow
					label="Paralegal mode"
					description="Adjust narrative style and formatting for paralegal billing"
				>
					<Switch
						checked={settings.paralegal_mode}
						onCheckedChange={(v) => update({ paralegal_mode: v })}
					/>
				</SettingRow>
			</SettingGroup>

			<SettingGroup label="General">
				<SettingRow
					label="Track entries after export"
					description="Entries will continue to be tracked even after they are exported"
				>
					<Switch
						checked={settings.track_after_exported}
						onCheckedChange={(v) => update({ track_after_exported: v })}
					/>
				</SettingRow>
				<SettingRow
					label="Auto-populate last used matter"
					description="Automatically fill in the matter from your most recent entry"
				>
					<Switch
						checked={settings.auto_populate_last_used_matter}
						onCheckedChange={(v) =>
							update({ auto_populate_last_used_matter: v })
						}
					/>
				</SettingRow>
			</SettingGroup>

			<SettingGroup label="Emails">
				<SettingRow
					label="Tracking threshold (seconds)"
					description="How long you must spend on an email before it starts being tracked"
				>
					<StepInput {...emailTime} />
				</SettingRow>
				<InternalEmailToggles
					trackInternal={settings.p_track_internal_emails}
					includeInNarratives={settings.p_include_internal_in_narratives}
					update={update}
				/>
				<EmailGroupingToggles
					mode={settings.email_grouping_mode}
					update={update}
				/>
			</SettingGroup>

			<SettingGroup label="Documents">
				<SettingRow
					label="Tracking threshold (seconds)"
					description="How long you must spend on a document before it starts being tracked"
				>
					<StepInput {...docTime} />
				</SettingRow>
				<SettingRow
					label="Add page numbers to narratives"
					description="Append page or slide counts to generated narratives (e.g. '5 pages')."
				>
					<Switch
						checked={settings.add_page_numbers}
						onCheckedChange={(v) => update({ add_page_numbers: v })}
					/>
				</SettingRow>
			</SettingGroup>

			<SettingGroup label="Browser">
				<SettingRow
					label="Tracking threshold (seconds)"
					description="How long you must spend on a page before it starts being tracked"
				>
					<StepInput {...websiteTime} />
				</SettingRow>
				<SettingRow
					label="Session inactivity timeout (minutes)"
					description="How long you must be inactive before browsing is tracked as a new entry"
				>
					<StepInput {...inactivityThreshold} />
				</SettingRow>
			</SettingGroup>

			<SettingGroup label="Meetings">
				<SettingRow
					label="Time to track meetings (seconds)"
					description="How long you must spend in a meeting before it starts being tracked"
				>
					<StepInput {...meetingTime} />
				</SettingRow>
			</SettingGroup>
		</>
	);
}
